import { Box, Gap, Icon, Tooltip } from '@salecycle/ui-component-library';
import UserLink from 'core/ui/components/userLink';
import { ICampaign } from 'entities/campaign';
import moment from 'moment-timezone';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getFormattedTimeRange } from 'sections/utils/time-range';
import CampaignActionsButton from '../../components/CampaignActionsButton';
import StateBadge from '../../components/StateBadge';
import { campaignTypeToIcon } from '../utils/campaignTypeToIcon';
import { StyledCampaignListItem, StyledCircleBadge } from './styles';

interface IProps {
  campaign: ICampaign;
  siteId: string;
  priorities: string[];
  timezone: string;
}

const isOffsiteCampaign = (campaign: ICampaign) =>
  campaign && campaign.type && (campaign.type === 'Browse Abandonment' || campaign.type === 'Cart Abandonment');

const CampaignListItem: FC<IProps> = ({ campaign, priorities, siteId, timezone }) => {
  const { t } = useTranslation('campaigns');
  const rulesetCount = campaign.rulesetPriorities?.length ?? 0;

  return (
    <StyledCampaignListItem data-testid="CampaignListItem">
      <div>
        <h3>
          {isOffsiteCampaign(campaign) ? (
            <Link to={`/sites/${siteId}/campaigns/offsite/${campaign.id}`}>{campaign.name}</Link>
          ) : (
            <Link to={`/sites/${siteId}/campaigns/campaigns/${campaign.id}`}>{campaign.name}</Link>
          )}
        </h3>
        <p>{getFormattedTimeRange(campaign.startTime, campaign.endTime, timezone, t)}</p>
        <p className="last-edit">
          <UserLink
            id={campaign.audit.lastUpdatedBy}
            suffix={t('campaigns:lastEditBy')}
            placeholder={t('campaigns:lastEdit')}
          />{' '}
          {moment(campaign.audit.lastUpdatedAt)
            .tz(timezone)
            .fromNow()}
        </p>
        <Gap top={25} />
        <Box>
          <StateBadge lifeCycle={campaign} />
          <Gap right={12} />
          {priorities.indexOf(campaign.id) > -1 && (
            <StyledCircleBadge>{(priorities.indexOf(campaign.id) + 1).toString()}</StyledCircleBadge>
          )}
          <Gap right={12} />
          {campaign.type && (
            <div>
              <Tooltip html={campaign.type} distance={15}>
                <StyledCircleBadge>
                  <Icon icon={campaignTypeToIcon(campaign.type)} />
                </StyledCircleBadge>
              </Tooltip>
            </div>
          )}
        </Box>
        <Gap top={15} />
        <p className="ruleset-info">{t('rulesetCount', { count: rulesetCount })}</p>
      </div>

      <CampaignActionsButton
        campaignId={campaign.id}
        campaign={campaign}
        siteId={siteId}
        editEnabled={true}
        cloneEnabled={true}
      />
    </StyledCampaignListItem>
  );
};

export default CampaignListItem;
